/*!
 * Copyright © 2022-2023. Verizon Connect Ireland Limited. All rights reserved.
 */

import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FmCookieService } from '@fleetmatics/ui.utilities';
import { Observable, of } from 'rxjs';
import { catchError, delay } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { IDeviceCommandResponse, IVehicleWithBoxResponse, IVehicleWithEsnResponse } from '../../../core/models';
import { IPaginatedDeviceHealthCheckStatusResponse } from '../../../device-health-check/models';
import { IDeviceHealthCheckHttpService } from './device-health-check-http.service.interface';

@Injectable({
  providedIn: 'root'
})
export class DeviceHealthCheckHttpService implements IDeviceHealthCheckHttpService {
  private readonly _vehicleApiBaseUrl: string;
  private readonly _deviceInstallApiBaseUrl: string;
  private readonly _liveMapDeviceHealthCheckClientContext = 'LiveMap-DeviceHealthCheck';

  constructor(private readonly _httpClient: HttpClient, private readonly _cookieService: FmCookieService) {
    this._vehicleApiBaseUrl = environment.config.endpoints.vehicleApiEndpoint;
    this._deviceInstallApiBaseUrl = environment.config.endpoints.deviceInstallApiEndpoint;
  }

  getVehicleSpecification(vehicleId: number): Observable<IVehicleWithBoxResponse> {
    return this._httpClient
      .get<IVehicleWithBoxResponse>(`${this._vehicleApiBaseUrl}/v1/Vehicles/${vehicleId}/specification`)
      .pipe(catchError((err: HttpErrorResponse) => of(null)));
  }

  getVehicleWithEsn(vehicleId: number): Observable<IVehicleWithEsnResponse> {
    return this._httpClient
      .get<IVehicleWithEsnResponse>(`${this._vehicleApiBaseUrl}/v1/Vehicles/${vehicleId}`)
      .pipe(catchError((err: HttpErrorResponse) => of(null)));
  }

  sendPing(esn: string, boxId: number): Observable<IDeviceCommandResponse> {
    return this._httpClient
      .post<IDeviceCommandResponse>(`${this._deviceInstallApiBaseUrl}/v2/Tracker/${this._getMockEsn(esn)}/ping`, {
        boxId: boxId,
        clientContext: {
          label: this._liveMapDeviceHealthCheckClientContext
        }
      })
      .pipe(catchError((err: HttpErrorResponse) => of(null)));
  }

  sendReset(esn: string, boxId: number, pingTransactionId: string): Observable<IDeviceCommandResponse> {
    // Check for 'mockDHCResetResponse' cookie. If it exists with value 'true' we don't actually send a reset command and just mock a success response
    const mockDHCResetResponse = this._cookieService.getCookie('mockDHCResetResponse') === 'true';

    if (mockDHCResetResponse) {
      const mockResponse: IDeviceCommandResponse = {
        code: 2000,
        message: 'Success',
        timestamp: new Date().toISOString(),
        transactionId: pingTransactionId
      };
      return of(mockResponse).pipe(delay(3000));
    }

    return this._httpClient
      .post<IDeviceCommandResponse>(`${this._deviceInstallApiBaseUrl}/v2/Tracker/${this._getMockEsn(esn)}/reset`, {
        boxId: boxId,
        transactionID: pingTransactionId
      })
      .pipe(catchError((err: HttpErrorResponse) => of(null)));
  }

  getDeviceHealthCheckStatus(page: number, size: number, esn: string): Observable<IPaginatedDeviceHealthCheckStatusResponse> {
    const params = new HttpParams()
      .append('Page', page)
      .append('Size', size)
      .append('ClientContext', this._liveMapDeviceHealthCheckClientContext);

    return this._httpClient
      .get<IPaginatedDeviceHealthCheckStatusResponse>(
        `${this._deviceInstallApiBaseUrl}/v2/Tracker/${this._getMockEsn(esn)}/deviceeventsgroupedbycontext`,
        {
          params
        }
      )
      .pipe(catchError((err: HttpErrorResponse) => of(null)));
  }

  private _getMockEsn(realEsn: string): string {
    const mockEsn = this._cookieService.getCookie('mockDHCEsn');

    return !mockEsn || mockEsn.trim() === '' ? realEsn : mockEsn;
  }
}
